import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { modalBodyClass } from "@/components/booking/exportDocumentModals/shared";

export default function FormModalShell({
  open,
  onClose,
  title,
  eyebrow,
  icon: Icon,
  refLabel,
  children,
  maxWidth = "max-w-2xl",
  footer,
  bodyClassName,
  footerClassName,
  hideDepthStrip = false,
  closeOnOverlayClick = true,
}) {
  const modal = (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/55 backdrop-blur-[3px]"
            onClick={closeOnOverlayClick ? onClose : undefined}
          />

          <div className="flex min-h-full items-start justify-center px-3 py-6 sm:px-4 sm:py-8">
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={`relative z-10 my-auto flex w-full ${maxWidth} max-h-[calc(100vh-3rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_64px_-16px_rgba(15,23,42,0.45)] ring-1 ring-black/5 dark:bg-slate-900 dark:ring-white/10`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-20 shrink-0 isolate">
              <div className="relative border-b border-slate-300/60 bg-gradient-to-b from-slate-50 via-slate-100/95 to-slate-200/70 px-4 py-2.5 backdrop-blur-xl dark:border-slate-600/50 dark:from-slate-800 dark:via-slate-800/95 dark:to-slate-900/80">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 overflow-hidden"
                >
                  <div className="absolute -left-6 top-1/2 h-16 w-24 -translate-y-1/2 rounded-full bg-white/50 blur-2xl dark:bg-slate-600/30" />
                  <div className="absolute -right-4 top-0 h-12 w-20 rounded-full bg-slate-300/40 blur-xl dark:bg-slate-500/20" />
                </div>

                <div className="relative flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    {Icon ? (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0d9488] shadow-[0_2px_6px_rgba(37,99,235,0.35)] ring-1 ring-blue-500/20">
                        <Icon className="h-4 w-4 text-white" strokeWidth={2.25} />
                      </div>
                    ) : null}
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5">
                      <h2 className="truncate text-[14px] font-semibold tracking-tight text-slate-800 dark:text-slate-100">
                        {title}
                      </h2>
                      {eyebrow ? (
                        <span className="hidden h-3.5 w-px bg-slate-300 dark:bg-slate-600 sm:block" />
                      ) : null}
                      {eyebrow ? (
                        <p className="truncate text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          {eyebrow}
                        </p>
                      ) : null}
                      {refLabel ? (
                        <>
                          <span className="hidden h-3.5 w-px bg-slate-300 dark:bg-slate-600 sm:block" />
                          <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                            Ref{" "}
                            <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                              {refLabel}
                            </span>
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/80 hover:text-slate-700 hover:shadow-sm dark:hover:bg-slate-700 dark:hover:text-slate-200"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {!hideDepthStrip ? (
                <div
                  aria-hidden
                  className="pointer-events-none h-3 bg-gradient-to-b from-slate-900/[0.08] via-slate-900/[0.03] to-transparent dark:from-black/35 dark:via-black/15"
                />
              ) : null}
            </div>

            <div className={bodyClassName ?? modalBodyClass}>{children}</div>

            {footer ? (
              <div
                className={
                  footerClassName ??
                  "shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-900"
                }
              >
                {footer}
              </div>
            ) : null}
          </motion.div>
          </div>
        </div>
      ) : null}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
